import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle2, Mail } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Turnstile, TURNSTILE_ENABLED } from '@/shared/ui/turnstile'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import { cn } from '@/shared/lib/cn'
import { subscribe } from './api'
import { newsletterSchema, type NewsletterInput } from './schema'

const T = defineT({
  emailLabel: { ar: 'البريد الإلكتروني', en: 'Email' },
  emailPlaceholder: { ar: 'name@example.com', en: 'name@example.com' },
  emailInvalid: {
    ar: 'البريد الإلكتروني غير صحيح',
    en: 'Invalid email address',
  },
  cta: { ar: 'اشترك', en: 'Subscribe' },
  privacy: {
    ar: 'لا نشارك بياناتك مع أي طرف ثالث.',
    en: "We never share your email with anyone.",
  },
  successTitle: { ar: 'تم الاشتراك', en: "You're in" },
  successBody: {
    ar: 'هتلاقي أحدث الكورسات والأدوات في إيميلك أول بأول.',
    en: 'You will get new courses and tools in your inbox.',
  },
  genericError: {
    ar: 'تعذّر الاشتراك. حاول مرة ثانية.',
    en: 'Could not subscribe. Please try again.',
  },
})

interface NewsletterFormProps {
  /** Visual variant. `inline` is the hero-style on dark CTA cards. */
  variant?: 'inline'
  className?: string
}

/**
 * Reusable newsletter subscribe form. Handles validation, network
 * error, success acknowledgement, and reduced-motion fallback. The
 * privacy line is rendered inside the form so it always stays paired
 * with the consent point — never decoupled across layouts.
 */
export function NewsletterForm({ variant = 'inline', className }: NewsletterFormProps) {
  const { t, isRtl } = useTranslation(T)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  const form = useForm<NewsletterInput>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: { email: '' },
  })

  const mutation = useMutation({
    mutationFn: (input: NewsletterInput) =>
      subscribe({ ...input, turnstileToken: turnstileToken ?? undefined }),
    onSuccess: (response) => {
      if (!response.success) {
        form.setError('email', {
          message: response.error.message ?? t('genericError'),
        })
      }
    },
    onError: () => {
      form.setError('email', { message: t('genericError') })
    },
  })

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values))

  const success = mutation.data?.success === true

  if (success) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'flex items-start gap-3 rounded-2xl border border-success/30 bg-success/10 p-4 text-success',
          className,
        )}
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div className="space-y-0.5">
          <p className="text-sm font-semibold">{t('successTitle')}</p>
          <p className="text-xs leading-relaxed text-success/80">
            {t('successBody')}
          </p>
        </div>
      </div>
    )
  }

  const error = form.formState.errors.email?.message

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className={cn('space-y-3', className)}
      aria-label={t('cta')}
      aria-busy={mutation.isPending}
    >
      <label htmlFor="newsletter-email" className="sr-only">
        {t('emailLabel')}
      </label>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
        <div className="relative flex-1">
          <Mail
            className="pointer-events-none absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            id="newsletter-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            dir="ltr"
            placeholder={t('emailPlaceholder')}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'newsletter-error' : 'newsletter-privacy'}
            className={cn(
              'h-14 w-full rounded-xl border bg-background px-12 text-sm',
              'placeholder:text-muted-foreground',
              'focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              'transition-colors duration-200',
              error ? 'border-destructive' : 'border-border',
            )}
            {...form.register('email')}
          />
        </div>
        <Button
          type="submit"
          size="lg"
          loading={mutation.isPending}
          disabled={TURNSTILE_ENABLED && !turnstileToken}
        >
          {t('cta')}
          <ArrowLeft
            className={cn('h-4 w-4', !isRtl && 'rotate-180')}
            aria-hidden
          />
        </Button>
      </div>
      {TURNSTILE_ENABLED && (
        <Turnstile
          action="newsletter"
          onVerify={setTurnstileToken}
          onExpire={() => setTurnstileToken(null)}
          onError={() => setTurnstileToken(null)}
        />
      )}
      {error ? (
        <p id="newsletter-error" role="alert" className="text-xs font-medium text-destructive">
          {error}
        </p>
      ) : (
        <p id="newsletter-privacy" className="text-xs text-muted-foreground">
          {t('privacy')}
        </p>
      )}
      {variant === 'inline' && null}
    </form>
  )
}
